import { useHandler } from "@chatbotgang/etude/react/useHandler";
import InputAdornment from "@mui/material/InputAdornment";
import type { SxProps } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import { WEBSTREAM_DOMAIN } from "@polifonia/env";
import type { muiTheme } from "@polifonia/theme";
import { useMutation } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Typography } from "@/components/Typography";
import { handleNonGlobalApiError } from "@/features/error/handleError";
import { interlude, interludeClient } from "@/interlude";
import { ChannelQueriesContext } from "@/pages/channels/pages/detail/channelQueriesContext";
import {
  isValidSubdomain,
  normalizeSubdomain,
} from "@/pages/channels/pages/detail/wccs/WebStreamConfig/subdomainFormat";

const styles = {
  field: {
    maxWidth: "480px",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

interface SubdomainFormValues {
  subdomain: string;
}

export const SubdomainEdit: React.FC = function SubdomainEdit() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { channel } = ChannelQueriesContext.useData();
  // Only rendered under the web-streams settings page (WebStreamConfigGuard
  // already checks this), but the narrowing doesn't cross component
  // boundaries, so re-check here before reading channelInformation.
  const isWccs = channel.type === "wccs";

  const { control, getValues, setValue, setError, clearErrors } =
    useForm<SubdomainFormValues>({
      mode: "onChange",
      defaultValues: {
        subdomain: (isWccs ? channel.channelInformation.subdomain : "") ?? "",
      },
    });

  const handleSubdomainApiError = useHandler((error: unknown) =>
    handleNonGlobalApiError(error, {
      CHANNEL_SUBDOMAIN_INVALID() {
        setError("subdomain", {
          message: t("webStreamConfig.general.subdomain.error.invalidFormat"),
        });
      },
      CHANNEL_SUBDOMAIN_ALREADY_EXISTS() {
        setError("subdomain", {
          message: t("webStreamConfig.general.subdomain.error.taken"),
        });
      },
    }),
  );

  const updateMutation = interlude.channel.useUpdateChannel(
    { params: { channelId: channel.id } },
    {
      onSuccess() {
        clearErrors("subdomain");
        enqueueSnackbar(t("common.updatedSuccessfully"), {
          variant: "success",
        });
      },
      onError: handleSubdomainApiError,
    },
  );

  const availabilityMutation = useMutation({
    mutationFn: (subdomain: string) =>
      interludeClient.channel.checkSubdomainAvailability({
        params: { channelId: channel.id },
        queries: { subdomain },
      }),
    onSuccess(result, checkedSubdomain) {
      // Discard a stale result if the field moved on again while this
      // particular check was in flight.
      if (getValues("subdomain") !== checkedSubdomain) return;
      if (!result.available) {
        setError("subdomain", {
          message: t("webStreamConfig.general.subdomain.error.taken"),
        });
        return;
      }
      clearErrors("subdomain");
      updateMutation.mutate({
        channelInformation: { subdomain: checkedSubdomain },
      });
    },
    onError: handleSubdomainApiError,
  });

  const onBlur = useHandler<React.ComponentProps<typeof TextField>["onBlur"]>(
    (e) => {
      const persisted =
        (isWccs ? channel.channelInformation.subdomain : "") ?? "";
      const normalized = normalizeSubdomain(e.target.value);
      if (normalized !== e.target.value) {
        // No `shouldValidate` — this field has no RHF rules/resolver, so
        // that flag's validation pass finds "no errors" and clears whatever
        // setError below sets, racing it out.
        setValue("subdomain", normalized);
      }
      if (normalized === persisted) {
        clearErrors("subdomain");
        return;
      }
      if (normalized === "") {
        // "" is an explicit, valid "clear the subdomain" value on save
        // (interlude PR #1216) — distinct from an unset field, and the
        // availability endpoint 400s on an empty query, so this must skip
        // straight to save rather than going through the check.
        updateMutation.mutate({ channelInformation: { subdomain: "" } });
        return;
      }
      if (!isValidSubdomain(normalized)) {
        setError("subdomain", {
          message: t("webStreamConfig.general.subdomain.error.invalidFormat"),
        });
        return;
      }
      availabilityMutation.mutate(normalized);
    },
  );

  if (!isWccs) return null;

  const isPending = availabilityMutation.isPending || updateMutation.isPending;

  return (
    <Controller
      name="subdomain"
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          size="small"
          sx={styles.field}
          placeholder={t("webStreamConfig.general.subdomain.placeholder")}
          onBlur={(e) => {
            field.onBlur();
            onBlur(e);
          }}
          error={!!error}
          helperText={error?.message}
          disabled={isPending}
          slotProps={{
            htmlInput: { maxLength: 63 },
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <Typography variant="note">.{WEBSTREAM_DOMAIN}</Typography>
                </InputAdornment>
              ),
            },
          }}
        />
      )}
    />
  );
};
